// medplum-messaging.js — ONLINE-PRIMARY messaging + Spaces read/execute, direct
// from the browser as the logged-in user (RLS the only lock). This is a 1:1 port
// of the FHIR calls in server/opd/messaging.mjs and the pure-FHIR halves of
// server/opd/spaces.mjs (GET /spaces/thread, POST /spaces/execute) — the SAME
// run-as-you Communication reads/writes those routes already make, moved off the
// Express proxy onto the browser's own MedplumClient (the queue/reception/reports
// recipe). Messaging.jsx / Spaces.jsx keep their Express calls as a fallback in
// this wave (bridge-level Medplum-first→Express idiom), so these functions must
// return the EXACT DTO shapes the screens already render.
//
// Thread model (Medplum messaging-data-model.mdx — verified identical to
// messaging.mjs): a thread HEADER Communication has NO payload and NO partOf
// (`part-of:missing=true` selects headers); a MESSAGE Communication has payload
// (contentString) + partOf:[header] (`part-of=Communication/<id>` selects a
// thread's messages). Fields (topic/subject/recipient/sender/status/category)
// map 1:1 — no translation.
//
// Lock-1 DROPPED: messaging.mjs's canSeeThread()/the spaces patient gate are
// deliberately NOT ported — the redesign's invariant is that every read here runs
// through the doctor's own token, so Medplum's AccessPolicy (RLS) is the only lock
// and an app-side per-row re-check must never be re-added (AGENTS.md §2 redesign
// box). The client sees exactly what its policy returns; nothing more to filter.
import { getMedplum } from './medplum-client.js';
import { randomUuid } from './outbox-ops.js';

// ── Messaging category (patient-channel) — verbatim from messaging.mjs. ──
const CATEGORY = [{
  coding: [{ system: 'https://healther.app/fhir/CodeSystem/communication-category', code: 'patient-channel', display: 'Patient channel' }],
  text: 'patient-channel',
}];
const MRN_SYSTEM = 'https://healther.io/opd/mrn';
const HEALTHER_BLOB_EXT = 'http://healther.io/fhir/StructureDefinition/opd-healther';

// ── Spaces (AI conversation) constants — verbatim from spaces.mjs. Used by the
// two pure-FHIR Spaces functions below; the topic identifier lets a reload reuse
// the same per-(clinician,patient) thread. ──
const SPACES_TOPIC_SYSTEM = 'http://healther.io/fhir/spaces-thread';
const SPACES_HISTORY_CAP = 40; // most-recent turns loaded on resume
const COMMAND_ACTION_IDENTIFIER_SYSTEM = 'https://healther.io/opd/command-action';

const refDisplay = (r) => r?.display || (r?.reference || '').replace(/^[^/]+\//, '') || 'Unknown';

// The sender ref for the logged-in clinician, read from the browser's Medplum
// profile (the equivalent of messaging.mjs's senderOf(staff) — there staff came
// from the Express session; here it's the doctor's own Practitioner resource).
function senderOf(medplum) {
  const p = medplum.getProfile();
  return {
    ...(p ? { reference: `${p.resourceType}/${p.id}` } : {}),
    display: p?.name?.[0]?.text
      || [p?.name?.[0]?.given?.join(' '), p?.name?.[0]?.family].filter(Boolean).join(' ')
      || 'Clinician',
  };
}

/** The channel a Communication is on, read from FHIR `medium` (gupshup-bridge
 *  stamps WRITTEN/<channel>) — 'whatsapp', 'sms', or 'in-app' (default, the app's
 *  own Communications carry no `medium`). Verbatim from messaging.mjs. */
function channelOf(comm) {
  const m = (comm?.medium || [])
    .map((x) => String(x.coding?.[0]?.display || x.coding?.[0]?.code || x.text || '').toLowerCase())
    .join(' ');
  if (m.includes('whatsapp')) return 'whatsapp';
  if (m.includes('sms')) return 'sms';
  return 'in-app';
}

/** Conversation key: ALL of a patient's channels collapse into ONE conversation
 *  keyed by the patient (`patient:<id>`); a subjectless / group thread keys on its
 *  own header id. This is the consolidation — one row per patient. */
function convKey(header) {
  const ref = header.subject?.reference || '';
  return ref.startsWith('Patient/') ? `patient:${ref.split('/')[1]}` : `thread:${header.id}`;
}

/** The title shown for a consolidated conversation. Verbatim from messaging.mjs. */
function convTitle(subjectRef, primary) {
  if (subjectRef) {
    const disp = subjectRef.display;
    if (disp) return disp;
    const t = primary?.topic?.text || '';
    if (t && !/^Patient channel/i.test(t)) return t;
    return refDisplay(subjectRef);
  }
  return primary?.topic?.text || '(no subject)';
}

/** Last message across a set of header ids (for the inbox preview), with its channel.
 *  ONE search across ALL of the conversation's channel headers — comma-OR on the
 *  `part-of` reference param, newest-first, top 1 — returns the single most-recent
 *  message across every channel. This replaces the previous one-search-PER-header
 *  loop: against cloud-latency Medplum that sequential N+1 (compounded by the
 *  per-conversation loop in listThreadsFromMedplum) dominated the inbox load and the
 *  unread-threads rail widget on every screen. `part-of` OR + `_sort=-sent&_count=1`
 *  yields exactly the `best` the loop computed. */
async function lastAcross(medplum, headerIds) {
  if (!headerIds.length) return null;
  const partOf = 'part-of=' + headerIds.map((id) => `Communication/${id}`).join(',');
  try {
    const msgs = await medplum.searchResources('Communication', `${partOf}&_sort=-sent&_count=1`);
    return msgs?.[0] || null;
  } catch (_) { return null; /* best-effort preview */ }
}

/** Summarize ONE consolidated conversation (one or more channel headers for the
 *  same patient) into a single inbox row. Verbatim from messaging.mjs. */
async function summarizeConversation(medplum, headers) {
  const primary = headers.find((h) => h.topic && !/^Patient channel/i.test(h.topic.text || '')) || headers[0];
  const headerIds = headers.map((h) => h.id);
  const subjectRef = headers.find((h) => h.subject?.reference)?.subject || null;
  const channels = [...new Set(headers.map((h) => channelOf(h)).filter((c) => c !== 'in-app'))];
  if (headers.some((h) => channelOf(h) === 'in-app')) channels.unshift('in-app');
  const last = await lastAcross(medplum, headerIds);
  const seen = new Set();
  const participants = [];
  for (const h of headers) for (const r of (h.recipient || [])) {
    const key = r.reference || r.display || '';
    if (key && !seen.has(key)) { seen.add(key); participants.push({ reference: r.reference || null, display: refDisplay(r) }); }
  }
  return {
    id: convKey(primary),
    headerIds,
    topic: convTitle(subjectRef, primary),
    status: headers.every((h) => h.status === 'completed') ? 'completed' : 'in-progress',
    subject: subjectRef ? { reference: subjectRef.reference, display: refDisplay(subjectRef) } : null,
    participants,
    channels: [...new Set(channels)],
    lastMessage: last ? { text: last.payload?.[0]?.contentString || '', at: last.sent || last.meta?.lastUpdated || null, sender: refDisplay(last.sender), channel: channelOf(last) } : null,
    updatedAt: last?.sent || headers.map((h) => h.meta?.lastUpdated).filter(Boolean).sort().pop() || null,
  };
}

/**
 * Thread-list DTO Messaging.jsx renders: { threads:[{id,topic,status,subject,
 * participants,channels,lastMessage,updatedAt}] }. Ports messaging.mjs listThreads()
 * verbatim MINUS the Lock-1 canSeeThread() filter (RLS is the only lock now).
 *   { patient }  - optional Medplum patient UUID to scope to one patient
 *   { mine }     - optional clinician display name (Messaging.jsx passes ME) to
 *                  keep only threads this clinician sent/receives, mirroring the
 *                  server's `mine` filter on refDisplay(sender)/recipient.
 */
export async function listThreadsFromMedplum({ patient = null, mine = null } = {}) {
  const { medplum } = await getMedplum();

  let q = 'part-of:missing=true&_sort=-_lastUpdated&_count=100';
  if (patient) q += `&subject=Patient/${patient}`;
  const headers = await medplum.searchResources('Communication', q);
  // Exclude Spaces' clinician↔AI headers (category ai-message / assistant-chat) — they
  // live as headerless Communications too but are NOT patient messaging threads. This
  // exclusion MUST be preserved or Spaces conversations leak into the inbox
  // (messaging.mjs:177 <-> spaces.mjs SPACES_CATEGORY 'ai-message').
  const excluded = (h) => (h.category || []).some((c) => /assistant-chat|ai-message/.test(String(c.text || c.coding?.[0]?.code || '')));
  const channel = (headers || []).filter((h) => !excluded(h) && ((h.category || []).some((c) => c.text === 'patient-channel') || h.topic));
  const filtered = mine ? channel.filter((h) => refDisplay(h.sender) === mine || (h.recipient || []).some((r) => refDisplay(r) === mine)) : channel;
  // CONSOLIDATE: group every header for the same patient into ONE conversation.
  const groups = new Map();
  for (const h of filtered) {
    const k = convKey(h);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(h);
  }
  // Summarize every consolidated conversation IN PARALLEL (each is one independent
  // last-message search) instead of sequentially — collapses G sequential round-trips
  // to one parallel wave, so the inbox depth is 2 (headers, then this wave).
  const out = await Promise.all([...groups.values()].map((hs) => summarizeConversation(medplum, hs)));
  out.sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
  return { threads: out };
}

/** Case-insensitive keep-first dedupe on a derived key. Preserves first-seen order. */
function dedupeByKey(items, keyFn) {
  const seen = new Set();
  const out = [];
  for (const it of items) {
    const k = keyFn(it);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(it);
  }
  return out;
}

/** Dedupe on the full normalized text (trim + lowercase). Near-dupes stay distinct. */
function dedupeByText(items) {
  return dedupeByKey(items, (t) => String(t).trim().toLowerCase());
}

/** A light patient context block for the thread's middle panel. Verbatim from messaging.mjs. */
async function patientContext(medplum, patientRef) {
  if (!patientRef?.startsWith?.('Patient/')) return null;
  const id = patientRef.split('/')[1];
  try {
    const p = await medplum.readResource('Patient', id);
    // A FAILED sentinel marker per list — the caller (Messaging.jsx's FactRow)
    // must be able to tell "search failed" apart from "search succeeded, zero
    // results", so a transient error never renders as the honest "None
    // recorded" empty state (m-11).
    const FAILED = Symbol('failed');
    const [allergiesRes, conditionsRes, medsRes] = await Promise.all([
      medplum.searchResources('AllergyIntolerance', `patient=Patient/${id}&_count=20`).catch(() => FAILED),
      medplum.searchResources('Condition', `patient=Patient/${id}&_count=20`).catch(() => FAILED),
      medplum.searchResources('MedicationRequest', `subject=Patient/${id}&_count=20`).catch(() => FAILED),
    ]);
    return {
      id,
      name: p.name?.[0]?.text || [p.name?.[0]?.given?.join(' '), p.name?.[0]?.family].filter(Boolean).join(' ') || 'Patient',
      birthDate: p.birthDate || null,
      gender: p.gender || null,
      mrn: (p.identifier || []).find((i) => i.system === MRN_SYSTEM)?.value || null,
      phone: (p.telecom || []).find((t) => t.system === 'phone')?.value || null,
      // null = the search itself failed (distinct from a real empty array).
      allergies: allergiesRes === FAILED ? null : dedupeByText(allergiesRes.map((a) => a.code?.text || a.code?.coding?.[0]?.display).filter(Boolean)),
      problems: conditionsRes === FAILED ? null : dedupeByText(conditionsRes.map((c) => c.code?.text || c.code?.coding?.[0]?.display).filter(Boolean)),
      // Meds dedupe on the DRUG NAME only (the leading token before dose/route),
      // case-insensitive, keep-first — the same drug repeats across visits/orders
      // but should show once. Problems dedupe on full normalized text, so
      // "…— improving control" vs "…— well controlled" are kept as distinct.
      medications: medsRes === FAILED ? null : dedupeByKey(
        medsRes.map((m) => m.medicationCodeableConcept?.text || m.medicationCodeableConcept?.coding?.[0]?.display).filter(Boolean),
        // Drug name = the leading run before the first dose digit or separator
        // (·, comma, dash, paren). "Metformin 500mg BD" and "metformin 850mg OD"
        // both key to "metformin" → shown once, first kept.
        (t) => String(t).split(/[\d·,\-–—(]/)[0].trim().toLowerCase(),
      ),
    };
  } catch (_) { return { id, name: 'Patient' }; }
}

/** Resolve a conversation id to its concrete header Communications. Verbatim from messaging.mjs. */
async function resolveHeaders(medplum, conversationId) {
  if (typeof conversationId === 'string' && conversationId.startsWith('patient:')) {
    const pid = conversationId.slice('patient:'.length);
    const headers = await medplum.searchResources('Communication', `subject=Patient/${pid}&part-of:missing=true&_count=100`);
    if (headers && headers.length) return headers;
    return [];
  }
  const id = typeof conversationId === 'string' && conversationId.startsWith('thread:') ? conversationId.slice('thread:'.length) : conversationId;
  const header = await medplum.readResource('Communication', id);
  return header ? [header] : [];
}

/**
 * Thread + messages DTO Messaging.jsx renders: { thread:{id,topic,status,subject,
 * participants,channels,createdAt}, messages:[{id,text,sender,senderRef,at,channel}],
 * patient:{...}|null }. Ports messaging.mjs getThread() verbatim MINUS the Lock-1 gate.
 * Throws a typed { status:404 } error for an unknown thread, same as the server.
 */
export async function getThreadFromMedplum(threadId) {
  const { medplum } = await getMedplum();

  const headers = await resolveHeaders(medplum, threadId);
  if (!headers.length) { const e = new Error('thread-not-found'); e.status = 404; throw e; }
  const subjectWithRef = headers.find((h) => h.subject?.reference);
  const subjectRef = subjectWithRef?.subject || headers.find((h) => h.subject)?.subject || null;
  const primary = headers.find((h) => h.topic && !/^Patient channel/i.test(h.topic.text || '')) || headers[0];
  const all = [];
  for (const h of headers) {
    const msgs = await medplum.searchResources('Communication', `part-of=Communication/${h.id}&_sort=sent&_count=500`);
    for (const m of (msgs || [])) all.push({
      id: m.id,
      text: m.payload?.[0]?.contentString || '',
      sender: refDisplay(m.sender),
      senderRef: m.sender?.reference || null,
      at: m.sent || m.meta?.lastUpdated || null,
      channel: channelOf(m),
    });
  }
  all.sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
  const seen = new Set();
  const participants = [];
  for (const h of headers) for (const r of (h.recipient || [])) {
    const key = r.reference || r.display || '';
    if (key && !seen.has(key)) { seen.add(key); participants.push({ reference: r.reference || null, display: refDisplay(r) }); }
  }
  return {
    thread: {
      id: typeof threadId === 'string' && (threadId.startsWith('patient:') || threadId.startsWith('thread:')) ? threadId : (subjectRef ? convKey(primary) : `thread:${primary.id}`),
      topic: convTitle(subjectRef, primary),
      status: headers.every((h) => h.status === 'completed') ? 'completed' : 'in-progress',
      subject: subjectRef ? { reference: subjectRef.reference, display: refDisplay(subjectRef) } : null,
      participants,
      channels: [...new Set(headers.map((h) => channelOf(h)).filter((c) => c !== 'in-app').concat(headers.some((h) => channelOf(h) === 'in-app') ? ['in-app'] : []))],
      createdAt: primary.meta?.lastUpdated || null,
    },
    messages: all,
    patient: subjectRef
      ? (subjectRef.reference ? await patientContext(medplum, subjectRef.reference) : { name: subjectRef.display || 'Patient' })
      : null,
  };
}

/** Pick the header a clinician's in-app reply attaches to. Verbatim from messaging.mjs. */
function writeHeader(headers) {
  return headers.find((h) => channelOf(h) === 'in-app') || headers[0];
}

/**
 * Post a message into a thread → the created message Communication. Ports
 * messaging.mjs postMessage() verbatim (in-app writeHeader() selection preserved)
 * MINUS the Lock-1 write gate.
 */
export async function postMessageToMedplum(threadId, text, { channel = 'in-app' } = {}) {
  const { medplum } = await getMedplum();

  const headers = await resolveHeaders(medplum, threadId);
  if (!headers.length) { const e = new Error('thread-not-found'); e.status = 404; throw e; }
  const external = channel === 'whatsapp';
  const header = external ? (headers.find((h) => channelOf(h) === 'whatsapp') || writeHeader(headers)) : writeHeader(headers);
  if (external && !header.subject?.reference?.startsWith('Patient/')) {
    throw new Error('WhatsApp replies require a patient-linked thread.');
  }
  let phone = null;
  if (external) {
    const patient = await patientContext(medplum, header.subject.reference);
    phone = patient?.phone || null;
    if (!phone) throw new Error('This patient has no mobile number for WhatsApp.');
  }
  const extension = external ? [{
    url: HEALTHER_BLOB_EXT,
    valueString: JSON.stringify({
      kind: 'patient-channel-outbound', direction: 'outbound', channel: 'whatsapp', toPhone: phone,
    }),
  }] : undefined;
  return medplum.createResource({
    resourceType: 'Communication', status: external ? 'preparation' : 'completed', category: CATEGORY,
    partOf: [{ reference: `Communication/${header.id}` }],
    ...(header.subject ? { subject: header.subject } : {}),
    ...(header.topic ? { topic: header.topic } : {}),
    sender: senderOf(medplum),
    recipient: external ? [{ reference: header.subject.reference }] : (header.recipient || undefined),
    ...(external ? {
      medium: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode', code: 'WRITTEN', display: 'whatsapp' }] }],
      extension,
    } : { sent: new Date().toISOString() }),
    payload: [{ contentString: String(text || '').slice(0, 4000) }],
  });
}

/**
 * Start a new thread → the created header Communication (with its firstMessage
 * posted). Ports messaging.mjs createThread() + the first-message post verbatim.
 *   { topic, patientId, firstMessage } — patientId is the Medplum patient UUID
 *   (Messaging.jsx's NewThreadModal already passes p.medplumId via pidOf()).
 */
export async function startThreadInMedplum({ topic, patientId = null, firstMessage = null, participants = [], idempotencyKey = null } = {}) {
  const { medplum } = await getMedplum();

  const retryKey = idempotencyKey === null ? null : String(idempotencyKey).trim().toLowerCase();
  if (retryKey !== null && !/^[a-z0-9][a-z0-9-]{0,79}$/.test(retryKey)) {
    throw new Error('invalid Communication idempotency key');
  }

  const recipient = [];
  if (patientId) recipient.push({ reference: `Patient/${patientId}` });
  for (const p of participants) recipient.push(typeof p === 'string' ? (p.includes('/') ? { reference: p } : { display: p }) : p);
  const sender = senderOf(medplum);
  recipient.push(sender); // the creator is always a participant
  const header = {
    resourceType: 'Communication', status: 'in-progress', category: CATEGORY,
    ...(retryKey ? { identifier: [{ system: COMMAND_ACTION_IDENTIFIER_SYSTEM, value: `${retryKey}-thread` }] } : {}),
    topic: { text: String(topic || 'New conversation').slice(0, 200) },
    ...(patientId ? { subject: { reference: `Patient/${patientId}` } } : {}),
    sender,
    recipient,
  };
  if (!firstMessage) return medplum.createResource(header);

  // Header + first message are one clinical operation. A sequential create left
  // an empty visible thread whenever the child write failed, and a retry created
  // a second header. A FHIR transaction either commits both Communications or
  // neither; the child's partOf resolves the header's urn inside the bundle.
  const headerUuid = randomUuid();
  const headerFullUrl = `urn:uuid:${headerUuid}`;
  const message = {
    resourceType: 'Communication', status: 'completed', category: CATEGORY,
    ...(retryKey ? { identifier: [{ system: COMMAND_ACTION_IDENTIFIER_SYSTEM, value: `${retryKey}-message` }] } : {}),
    partOf: [{ reference: headerFullUrl }],
    ...(header.subject ? { subject: header.subject } : {}),
    ...(header.topic ? { topic: header.topic } : {}),
    sender,
    recipient,
    sent: new Date().toISOString(),
    payload: [{ contentString: String(firstMessage).slice(0, 4000) }],
  };
  const response = await medplum.executeBatch({
    resourceType: 'Bundle',
    type: 'transaction',
    entry: [
      {
        fullUrl: headerFullUrl,
        resource: header,
        request: {
          method: 'POST',
          url: 'Communication',
          ...(retryKey ? { ifNoneExist: `identifier=${COMMAND_ACTION_IDENTIFIER_SYSTEM}|${encodeURIComponent(`${retryKey}-thread`)}` } : {}),
        },
      },
      {
        fullUrl: `urn:uuid:${randomUuid()}`,
        resource: message,
        request: {
          method: 'POST',
          url: 'Communication',
          ...(retryKey ? { ifNoneExist: `identifier=${COMMAND_ACTION_IDENTIFIER_SYSTEM}|${encodeURIComponent(`${retryKey}-message`)}` } : {}),
        },
      },
    ],
  });
  const first = response?.entry?.[0] || {};
  const location = String(first.response?.location || '');
  const id = first.resource?.id || (/Communication\/([^/]+)/.exec(location) || [])[1] || null;
  if (!id) throw new Error('Communication transaction returned no thread id.');
  return { ...(first.resource || header), id };
}

/** Update thread topic / participants / status across all its headers → the first
 *  header. Ports messaging.mjs patchThread() verbatim MINUS the Lock-1 write gate.
 *  Not wired into the fetch-swap yet (PATCH stays on Express this wave) but exported
 *  for parity so a later hardening pass can drop the Express PATCH too. */
export async function patchThreadInMedplum(threadId, { topic, addParticipant, removeParticipant, status } = {}) {
  const { medplum } = await getMedplum();

  const headers = await resolveHeaders(medplum, threadId);
  if (!headers.length) { const e = new Error('thread-not-found'); e.status = 404; throw e; }
  for (const header of headers) {
    const next = { ...header };
    if (topic !== undefined) next.topic = { text: String(topic || '').slice(0, 200) };
    if (status && ['in-progress', 'completed'].includes(status)) next.status = status;
    if (addParticipant) {
      const p = typeof addParticipant === 'string' ? (addParticipant.includes('/') ? { reference: addParticipant } : { display: addParticipant }) : addParticipant;
      next.recipient = [...(header.recipient || []), p];
    }
    if (removeParticipant) {
      next.recipient = (header.recipient || []).filter((r) => (r.reference || r.display) !== removeParticipant);
    }
    await medplum.updateResource(next);
  }
  return headers[0];
}

// ═══════════════════════════════════════════════════════════════════════════
// SPACES — the two PURE-FHIR halves (read + confirmed-write execution).
// POST /spaces/ask (the OpenAI agent loop) is NOT here: it holds OPENAI_API_KEY
// and MUST stay the thin Express secret-gateway (spaces.mjs:605). Only /spaces/
// thread and /spaces/execute are pure Communication/FHIR ops, portable today.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Load the persisted Spaces conversation for the signed-in clinician + the given
 * patient (or the panel thread when patientUuid is empty) → { turns:[...] }, the
 * exact DTO Spaces.jsx renders. Ports spaces.mjs GET /spaces/thread verbatim MINUS
 * the Lock-1 patient gate (RLS-only). The topic identifier is `${meId}:${patientUuid
 * || 'panel'}` — meId = the doctor's own Practitioner id (medplum.getProfile().id),
 * matching spaces.mjs's me.medplumPractitionerId. NOTE the caller must pass the
 * Medplum patient UUID (Spaces.jsx's picker rows carry .medplumId) — a disk slug
 * won't match the seeded topic identifier.
 */
export async function getSpaceThreadFromMedplum(patientUuid) {
  const { medplum } = await getMedplum();

  const profile = medplum.getProfile();
  const meId = profile?.id || null;
  const pid = String(patientUuid || '').trim() || null;
  const value = `${meId || 'anon'}:${pid || 'panel'}`;
  const topic = await medplum
    .searchOne('Communication', `identifier=${SPACES_TOPIC_SYSTEM}|${encodeURIComponent(value)}`)
    .catch(() => null);
  if (!topic) return { ok: true, turns: [] };

  // Newest-first from the server so the cap keeps the MOST-recent turns, then flip
  // to oldest-first for display (verbatim from spaces.mjs).
  const children = await medplum.searchResources('Communication',
    `part-of=Communication/${topic.id}&_sort=-sent&_count=${SPACES_HISTORY_CAP}`).catch(() => []);
  const turns = (children || [])
    .slice()
    .reverse()
    .map((c) => {
      try {
        const t = JSON.parse(c.payload?.[0]?.contentString || '{}');
        if (!t || !t.role) return null;
        return {
          turnId: c.id,
          role: t.role === 'assistant' ? 'assistant' : 'user',
          content: t.content || '',
          ...(t.steps ? { steps: t.steps } : {}),
          ...(t.proposedActions ? { proposedActions: t.proposedActions } : {}),
          ...(t.blocks ? { blocks: t.blocks } : {}),
          ...(t.choices ? { choices: t.choices } : {}),
          executed: !!t.executed,
          ...(t.results ? { results: t.results } : {}),
        };
      } catch (_) { return null; }
    })
    .filter(Boolean);
  return { ok: true, turns };
}

// ── FHIR path normalisation for /spaces/execute — verbatim from spaces.mjs
// (MRN-as-id guard, accurate-totals guard, malformed _search guard). Kept so the
// client-side execute resolves paths identically to the server. ──
const MRN_RE = /^UH-[A-Za-z0-9-]+$/;
const cleanStr = (v) => (v === null || v === undefined ? '' : String(v).trim());

function normaliseFhirPath(rawPath, method) {
  let p = cleanStr(rawPath).replace(/^\/+/, '').replace(/^fhir\/R4\//i, '').replace(/^R4\//i, '');
  if (!p) throw new Error('fhir_request: empty path');
  if (/(^|\/)_search(\b|\?|\/)/.test(p)) {
    throw new Error(`fhir_request: malformed path "${p}" — drop the "_search" artifact and use a plain "Type?param=value" query.`);
  }
  const qIdx = p.indexOf('?');
  let pathPart = qIdx >= 0 ? p.slice(0, qIdx) : p;
  let queryPart = qIdx >= 0 ? p.slice(qIdx + 1) : '';
  const segs = pathPart.split('/').filter(Boolean);
  const isGet = (cleanStr(method).toUpperCase() || 'GET') === 'GET';
  if (isGet && segs.length === 2 && segs[0] === 'Patient' && MRN_RE.test(segs[1])) {
    const mrn = segs[1];
    segs.length = 1;
    const idQ = `identifier=${encodeURIComponent(MRN_SYSTEM)}|${encodeURIComponent(mrn)}`;
    queryPart = queryPart ? `${idQ}&${queryPart}` : idQ;
  }
  const isCollectionRead = isGet && segs.length === 1;
  if (isCollectionRead) {
    const params = new URLSearchParams(queryPart);
    if (!params.has('_total') && !params.has('_summary')) params.set('_total', 'accurate');
    queryPart = params.toString();
  }
  return { segs, query: queryPart };
}

// Execute one confirmed FHIR write against Medplum as the logged-in user — verbatim
// from spaces.mjs runFhirRequest() (only the write methods are reachable from the
// execute route). fhirUrl(...) resolves against the CONFIGURED base.
async function runFhirRequest(medplum, { method, path, body }) {
  const m = cleanStr(method).toUpperCase() || 'GET';
  const { segs, query } = normaliseFhirPath(path, m);
  const url = medplum.fhirUrl(...segs);
  if (query) url.search = query;
  switch (m) {
    case 'GET': return medplum.get(url, { cache: 'no-cache' });
    case 'POST': return medplum.post(url, body || {});
    case 'PUT': return medplum.put(url, body || {});
    case 'PATCH': return medplum.patch(url, Array.isArray(body) ? body : (body ? [body] : []));
    case 'DELETE': return medplum.delete(url);
    default: throw new Error(`fhir_request: unsupported method ${m}`);
  }
}

/**
 * Execute the clinician-CONFIRMED writes from a propose_actions card → { ok, results }
 * (the DTO Spaces.jsx's ConfirmCard renders). Ports spaces.mjs POST /spaces/execute
 * verbatim: only POST/PUT/PATCH/DELETE run (GETs rejected), each as the logged-in
 * user so RLS applies, plus the mark-turn-executed guard so a reloaded proposal
 * renders read-only ("Changes applied") instead of re-offering the confirm card.
 * The server's reconcile/audit side-effects are main-1-only (disk mirror) and drop
 * away under the redesign — RLS + the mark-executed guard are the load-bearing parts.
 *   action = { turnId?, actions:[{ description, method, path, body }] }
 */
export async function executeSpaceActionInMedplum(action) {
  const { medplum } = await getMedplum();

  const actions = Array.isArray(action?.actions) ? action.actions.slice(0, 200) : [];
  if (!actions.length) { const e = new Error('no-actions'); e.status = 400; throw e; }

  const results = [];
  for (const a of actions) {
    const method = cleanStr(a?.method).toUpperCase();
    const path = cleanStr(a?.path);
    const description = cleanStr(a?.description) || `${method} ${path}`;
    if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      results.push({ description, ok: false, error: 'only confirmed write actions can be executed here' });
      continue;
    }
    try {
      const out = await runFhirRequest(medplum, { method, path, body: a?.body ?? null });
      results.push({ description, ok: true, ref: out?.resourceType ? `${out.resourceType}/${out.id}` : null });
    } catch (e) {
      const forbidden = e?.status === 403 || e?.outcome?.issue?.some?.((x) => x.code === 'forbidden')
        || /forbidden|not allowed|access policy|unauthorized/i.test(e?.message || '');
      results.push({ description, ok: false, error: forbidden ? 'access denied — you do not have permission for that' : e.message });
    }
  }

  // Mark the originating proposal turn EXECUTED so a later reload renders it read-only
  // instead of re-offering the confirm card (a duplicate-write guard). Best-effort:
  // a failure here never fails the response (verbatim intent from spaces.mjs).
  const turnId = cleanStr(action?.turnId);
  if (turnId) {
    try {
      const comm = await medplum.readResource('Communication', turnId);
      const t = JSON.parse(comm?.payload?.[0]?.contentString || '{}');
      t.executed = true;
      t.results = results.map((r) => ({ description: r.description, ok: r.ok, ...(r.ref ? { ref: r.ref } : {}), ...(r.error ? { error: r.error } : {}) }));
      await medplum.updateResource({ ...comm, payload: [{ contentString: JSON.stringify(t) }] });
    } catch (_) { /* non-fatal: the in-session card already shows 'done' */ }
  }

  return { ok: true, runAsUser: true, results };
}
